import React, { useState, useEffect } from 'react';
import './App.css';
import { CreateRootStore } from './models/RootStore';
import { CurrentUserInitializer, AccountNotProvisionedError } from './CurrentUserInitializer';
import Splash from './components/Splash/Splash';
import Home from './pages/Home';
import HomeV2 from './pagesv2/HomeV2';
import { Route, Switch, Redirect } from 'react-router-dom';
import { observer } from 'mobx-react';
import ErrorDisplay from './components/ErrorDisplay/ErrorDisplay';
import { CompanyInitializer } from './CompanyInitializer';
import { useUserContext } from './UserContextProvider';
import { useAuth0 } from '@auth0/auth0-react';
import InactivityTimer from './components/InactivityTimer';
import LoginView from './Login';
import * as Sentry from '@sentry/react';
import './utils/devSeedTeamData'; // Dev-only: registers window.seedTeamData()
import MurphyReportPreview from './components/MurphyReport/MurphyReportPreview';


const App = observer(() => {
  const { isAuthenticated, user } = useAuth0();

  // Belt-and-braces with AppWithAuth: never bounce a /share/* visitor to
  // LoginView, even if the parent bypass somehow didn't fire (HMR edge cases).
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/share/')) {
    return null;
  }

  if (!isAuthenticated) {
    return <LoginView />;
  }

  Sentry.setUser({ id: user?.sub?.split('|')[1], email: user?.email });

  return <AppInternal user={user} />;
});


const AppInternal = ({ user }) => {
  const [rootStore] = useState(CreateRootStore());
  const { userId } = useUserContext();
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState('');
  const [fatalError, setFatalError] = useState(null);
  const { logout, isAuthenticated } = useAuth0();
  const [sessionKey, setSessionKey] = useState(Date.now());

  // Reset sessionKey on every login
  useEffect(() => {
    if (isAuthenticated) {
      setSessionKey(Date.now());
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const initialize = async () => {
      try {
        const {
          usersStore,
          companyStore,
          assessmentsStore,
          adminStore,
          modulesStore
        } = rootStore;

        await CurrentUserInitializer(
          usersStore,
          adminStore,
          userId
        );

        // Use previously selected company from sessionStorage if available and valid,
        // otherwise default to the user's first company.
        // Admins can access any company (their companyIds may be empty).
        const savedCompanyId = sessionStorage.getItem('selectedCompanyId');
        const userCompanyIds = usersStore.currentUser.companyIds || [];
        const isAdmin = usersStore.currentUser.isAdmin;
        let companyId = savedCompanyId && (isAdmin || userCompanyIds.includes(savedCompanyId))
          ? savedCompanyId
          : userCompanyIds[0];

        // For admins with no companyIds, use the first company from adminStore
        if (!companyId && isAdmin && adminStore.companies.length > 0) {
          companyId = adminStore.companies[0].company.id;
        }

        if (!companyId) {
          console.error('User has no company IDs assigned. Contact your administrator.');
          setIsLoaded(true);
          return;
        }

        await CompanyInitializer(
          companyStore,
          assessmentsStore,
          usersStore,
          modulesStore,
          companyId
        );



        // Expose rootStore in dev for testing (browser console access)
        if (process.env.NODE_ENV === 'development') {
          window.__rootStore = rootStore;
        }

        setIsLoaded(true);
      } catch (err) {
        console.log('Error in initialization!');
        console.error(err);
        if (err instanceof AccountNotProvisionedError) {
          setFatalError({ kind: 'ACCOUNT_NOT_PROVISIONED', sub: user?.sub, userId: err.userId });
        } else {
          setFatalError({ kind: 'INIT_FAILED', message: err?.message || 'Initialization failed' });
        }
        setIsLoaded(true);
      }
    };

    initialize();
  }, [logout, rootStore, setError, setIsLoaded, userId, user?.sub]);

  if (fatalError?.kind === 'ACCOUNT_NOT_PROVISIONED') {
    return (
      <div data-testid='account-not-provisioned' style={{ maxWidth: 560, margin: '80px auto', padding: 24, fontFamily: 'inherit' }}>
        <h2 style={{ marginTop: 0 }}>We couldn't find your account</h2>
        <p>Your sign-in worked, but we don't have a Multiply user record for you yet. Please contact <a href="mailto:support@askmultiply.com">support@askmultiply.com</a> and share the reference below so we can sort it out.</p>
        <pre style={{ background: '#f4f4f4', padding: 12, borderRadius: 4, fontSize: 12, overflow: 'auto' }}>sub: {fatalError.sub || '(unknown)'}{'\n'}userId: {fatalError.userId || '(unknown)'}</pre>
        <button onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })} style={{ padding: '8px 14px', cursor: 'pointer' }}>Sign out</button>
      </div>
    );
  }

  if (fatalError?.kind === 'INIT_FAILED') {
    return (
      <div data-testid='init-failed' style={{ maxWidth: 560, margin: '80px auto', padding: 24, fontFamily: 'inherit' }}>
        <h2 style={{ marginTop: 0 }}>We couldn't load your account</h2>
        <p>Something went wrong while loading Multiply. Try refreshing the page, or sign out and back in.</p>
        <pre style={{ background: '#f4f4f4', padding: 12, borderRadius: 4, fontSize: 12, overflow: 'auto' }}>{fatalError.message}</pre>
        <button onClick={() => window.location.reload()} style={{ padding: '8px 14px', cursor: 'pointer', marginRight: 8 }}>Retry</button>
        <button onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })} style={{ padding: '8px 14px', cursor: 'pointer' }}>Sign out</button>
      </div>
    );
  }

  if (!isLoaded) {
    return <div data-testid='splash'>
      <ErrorDisplay message={error} />
      <Splash showLoader={true} />
    </div>;
  }

  return (
    <div style={{ height: '100%' }} data-testid='app'>
      <InactivityTimer key={user?.sub + '-' + sessionKey} logout={logout} /> {/* Remounts timer on every login */}
      <Switch>
        {/* Dev preview surface for the Murphy report PDF — sample data, used to
            verify the rendered layout. Gated to non-production builds so it
            doesn't ship to customer-facing prod, but reachable on
            dev/staging/sandbox for iteration. Uses REACT_APP_ENVIRONMENT
            (which the pipeline actually sets to 'development' / 'staging' /
            'production'); sandbox builds don't set this var, so the route
            stays visible there too. */}
        {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
          <Route path='/dev/murphy-report-preview' render={() => <MurphyReportPreview />} />
        )}
        <Redirect from="/v2/:rest+" to="/:rest+" />
        <Redirect from="/v2" to="/" exact />
        <Route
          path='/v1'
          render={(props) => <Home {...props} rootStore={rootStore} />}
        />
        <Route
          path='/'
          render={(props) => <HomeV2 {...props} rootStore={rootStore} />}
        />
      </Switch>
    </div>
  );
};

export default App;
